import { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';

const { logger } = Sentry;
import { getShareCode, getReferrals, GetAccessTokenSilently } from '../../services/api';
import './Sharing.scss';

function Sharing() {
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
  
  // Test mode: check localStorage for test mode flag
  const isTestMode = typeof window !== 'undefined' && 
    (localStorage.getItem('__TEST_MODE__') === 'true' || 
     window.location.search.includes('testMode=true'));
  const [shareData, setShareData] = useState<{
    shareCode: string;
    shareUrl: string;
    referralCreditsEarned: number;
    maxReferralCredits: number;
  } | null>(null);
  const [referrals, setReferrals] = useState<Array<{
    userId: string;
    name: string;
    email: string | null;
    createdAt: string;
    hasImportedJob: boolean;
    creditsEarned?: boolean;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Track if we've already loaded data to prevent duplicate calls
  const hasLoadedDataRef = useRef(false);
  const lastAuthStateRef = useRef<{ isAuthenticated: boolean; isLoading: boolean } | null>(null);

  useEffect(() => {
    const currentAuthState = { isAuthenticated: !!isAuthenticated, isLoading: !!isLoading };
    
    // Reset the flag if authentication state changed (e.g., logout/login)
    if (lastAuthStateRef.current && 
        (lastAuthStateRef.current.isAuthenticated !== currentAuthState.isAuthenticated ||
         lastAuthStateRef.current.isLoading !== currentAuthState.isLoading)) {
      hasLoadedDataRef.current = false;
    }
    lastAuthStateRef.current = currentAuthState;

    // Prevent duplicate calls in React StrictMode or when dependencies change unnecessarily
    if (hasLoadedDataRef.current) {
      return;
    }

    // In test mode, load immediately with mock token
    if (isTestMode) {
      const tokenFn = async () => 'mock-test-token';
      loadData(tokenFn as GetAccessTokenSilently);
      hasLoadedDataRef.current = true;
    } else if (!isLoading && isAuthenticated) {
      // Only load when authenticated (not in test mode)
      loadData(getAccessTokenSilently as GetAccessTokenSilently);
      hasLoadedDataRef.current = true;
    }
  }, [isTestMode, isAuthenticated, isLoading]); // Removed getAccessTokenSilently from dependencies

  const loadData = async (tokenFn: GetAccessTokenSilently = getAccessTokenSilently as GetAccessTokenSilently) => {
    try {
      setLoading(true);
      setError(null);
      // Type assertion needed because Auth0's getAccessTokenSilently has overloads
      // that TypeScript can't automatically narrow to our simpler type signature
      const [shareCodeData, referralsData] = await Promise.all([
        getShareCode(tokenFn),
        getReferrals(tokenFn)
      ]);
      setShareData(shareCodeData);
      setReferrals(referralsData.referrals);
    } catch (err) {
      logger.error('Error loading sharing data', {
        error: err instanceof Error ? err.message : String(err),
      });
      setError(err instanceof Error ? err.message : 'Failed to load sharing data');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = async () => {
    if (!shareData) return;
    
    try {
      await navigator.clipboard.writeText(shareData.shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error('Failed to copy', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  // Calculate credits earned from referrals list (count "Credits Earned" entries, capped at 20)
  const calculateCreditsEarned = (): number => {
    const creditsEarnedCount = referrals.filter(r => r.creditsEarned).length;
    return Math.min(creditsEarnedCount, 20); // Cap at 20
  };

  const creditsEarned = calculateCreditsEarned();
  const maxReferralCredits = 20;

  if (loading) {
    return (
      <div className="sharing-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sharing-container">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="sharing-page">
      <div className="sharing-wrapper">
        <div className="sharing-container">
          <div className="sharing-header">
            <h1 className="sharing-title">Invite Friends & Earn Credits</h1>
            <p className="sharing-subtitle">
              Share your unique link and earn 2 credits for each friend who signs up and imports at least one job. 
              Your friends also get 2 extra credits when they sign up using your link! 
              You can earn up to 20 credits total!
            </p>
          </div>

          {shareData && (
            <div className="share-card">
              <div className="card-header">
                <div className="card-header-content">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <h3 className="card-title">Your Share Link</h3>
                </div>
              </div>
              <div className="card-content">
                <div className="share-url-input">
                  <input
                    type="text"
                    className="url-input"
                    value={shareData.shareUrl}
                    readOnly
                  />
                  <button
                    className="btn-modern btn-primary"
                    onClick={handleCopyUrl}
                    type="button"
                  >
                    {copied ? (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                        Copy Link
                      </>
                    )}
                  </button>
                </div>
                <div className="share-code">
                  <span className="share-code-label">Your Share Code:</span>
                  <code className="share-code-value">{shareData.shareCode}</code>
                </div>
                <div className="progress-section">
                  <div className="progress-header">
                    <span className="progress-label">Credits Earned</span>
                    <span className="progress-count">{creditsEarned} / {maxReferralCredits}</span>
                  </div>
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: `${Math.max((creditsEarned / maxReferralCredits) * 100, creditsEarned > 0 ? 5 : 0)}%`
                      }}
                    >
                      {creditsEarned > 0 && (
                        <span className="progress-text">{creditsEarned} / {maxReferralCredits}</span>
                      )}
                    </div>
                    {creditsEarned === 0 && (
                      <span className="progress-text-empty">0 / {maxReferralCredits}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="referrals-section">
            <div className="section-header">
              <h2 className="section-title">People You've Invited</h2>
            </div>
            {referrals.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <h4 className="empty-title">No referrals yet</h4>
                <p className="empty-description">Share your link to get started!</p>
              </div>
            ) : (
              <div className="referrals-card">
                <div className="referrals-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Signed Up</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.map((referral) => (
                        <tr key={referral.userId}>
                          <td>{referral.name}</td>
                          <td>{referral.email || '-'}</td>
                          <td>{formatDate(referral.createdAt)}</td>
                          <td>
                            {referral.creditsEarned ? (
                              <span className="status-badge status-success">Credits Earned</span>
                            ) : referral.hasImportedJob ? (
                              <span className="status-badge status-info">Job Imported</span>
                            ) : (
                              <span className="status-badge status-warning">Pending</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Sharing;

